import _ from "lodash";
import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useMedia } from "react-media";
import { Form, Header, Segment, Grid, Placeholder } from "semantic-ui-react";
import { useHistory, useParams, Link } from "react-router-dom";
import { PublicWrapper } from "../../../../components/PublicWrapper";
import { useClient } from "../../../../hooks/use-client";
import { useCitizenPermitsTypes } from "../../hooks/hooks";
import { mixPanelAgencyOnly } from "../../../../mixpanel/general_functions";
import { AutocompleteTextBox, AutocompleteTextBoxItem } from "../../components/AutocompleteTextBox";

import "./PermitsPublicLanding.scss";
import StaffOnlyLabel from "../../components/StaffOnlyLabel";

interface PermitType {
  value: string;
  key: string;
  text: string;
}

const Landing = ({ site, style }: any) => {
  const history = useHistory();
  const isSmallScreen = useMedia({ query: "(max-width: 640px)" });
  const client = useClient(site);
  const cityName = useMemo(() => _.get(client.query, "data.payload.data.attributes.name"), [client.query.data]);
  const { data, query } = useCitizenPermitsTypes();
  const [filteredPermitsTypes, setFilteredPermitsTypes] = useState<PermitType[]>(data);
  const handleSearchChange = useCallback(
    (value: string) => {
      const needle = value.toLowerCase();
      setFilteredPermitsTypes(
        data
          .filter((permitType: any) => {
            return permitType.attributes.name.toLowerCase().includes(needle);
          })
          .map((permitType: any) => {
            return {
              value: permitType.id,
              key: permitType.id,
              text: permitType.attributes.name,
            };
          }),
      );
    },
    [data],
  );
  const isLoading = query.isLoading;
  const { data: permitsTypes } = useCitizenPermitsTypes();
  const numberOfPlaceholders = 5;
  useEffect(() => {
    mixPanelAgencyOnly(site, "Citizen Permit Apply Page", 1);
  }, []);

  useEffect(() => {
    document.title = `${cityName} Permits`;
  }, [cityName]);

  return (
    <div style={style}>
      <Segment className='landingSegment'>
        <Header as='h1' id='landingHeading'>
          Get Started on Your Permit Application Today
        </Header>
        <Form size={isSmallScreen ? "medium" : "huge"} role='search'>
          <Form.Field width={isSmallScreen ? undefined : 9}>
            <AutocompleteTextBox
              type='searchbox'
              inputProps={{
                "className": "tw-mt-2",
                "aria-labelledby": "landingHeading",
              }}
              placeholder='Search by permit name'
              items={filteredPermitsTypes}
              allowsCustomValue={false}
              onSelectionChange={(key) => {
                const permitType = filteredPermitsTypes.find((type) => type.key === key);

                if (permitType) {
                  history.push(`/${site}/public/permits/applications/${permitType.value}`);
                }
              }}
              onInputChange={_.debounce(handleSearchChange, 500, {
                leading: true,
              })}
            >
              {({ text, key }) => (
                <AutocompleteTextBoxItem key={key} size='large'>
                  {text}
                </AutocompleteTextBoxItem>
              )}
            </AutocompleteTextBox>
          </Form.Field>
        </Form>
      </Segment>
      <Segment className='landingSegment'>
        <Form className='PermitsTypeSelection'>
          <Header as='h2'>
            <Header.Content>All Permits</Header.Content>
          </Header>
          <Grid>
            <Grid.Column computer={16} tablet={16} mobile={16}>
              {isLoading
                ? Array.from({ length: numberOfPlaceholders }).map((_, index) => (
                    <Segment key={index}>
                      <Placeholder fluid>
                        <Placeholder.Header>
                          <Placeholder.Line />
                          <Placeholder.Line />
                        </Placeholder.Header>
                      </Placeholder>
                    </Segment>
                  ))
                : permitsTypes.map((permitType: any, index: any) => {
                    const staffOnly = permitType.attributes["staff-only"];
                    return (
                      <Link to={`/${site}/public/permits/applications/${permitType.id}`} key={index}>
                        <Segment color={staffOnly ? "grey" : undefined} textAlign='left' className='PermitSelect'>
                          <Header as='h3' className='permitsTitle'>
                            {permitType.attributes.name}
                            {staffOnly ? <StaffOnlyLabel></StaffOnlyLabel> : <div></div>}
                          </Header>
                          {permitType.attributes["short-description"] && (
                            <p className='permitsDescription'>{permitType.attributes["short-description"]}</p>
                          )}
                        </Segment>
                      </Link>
                    );
                  })}
            </Grid.Column>
          </Grid>
        </Form>
      </Segment>
    </div>
  );
};

export const PermitsPublicLanding = () => {
  const { site } = useParams<{ site: string }>();
  const isSmallScreen = useMedia({ query: "(max-width: 640px)" });

  return (
    <PublicWrapper homeUrl={`/${site}/public/permits`}>
      <Landing site={site} style={isSmallScreen ? { margin: "16px 0px" } : { margin: "48px 80px" }} />
    </PublicWrapper>
  );
};

export default {
  PermitsPublicLanding,
};
